import React, { useEffect, useMemo, useState } from 'react';
import QrScanner from './QrScanner';
import IncidentPicker from './IncidentPicker';
import DeploymentExpirationGate from './DeploymentExpirationGate';
import UsedSupplyReporter from './UsedSupplyReporter';
import GearInventoryView from './GearInventoryView';
import DoorAccessView, { DoorAccessTile } from './DoorAccessView';
import TransferGearView from './TransferGearView';
import useGearHandoffs from './useGearHandoffs';
import { callGearHandoff } from './gearHandoffApi';
import { WorkflowAlert, WorkflowComplete } from './WorkflowFeedback';
import useTrackingStore from './useTrackingStore';
import { getMemberAccessToken, supabase } from '../auth/supabase';
import { DEPLOYMENT_TYPES, RETURN_CONDITIONS, getDeploymentForAsset, normalizeAssetId } from './trackingModel';
import {
  activeDeploymentsForPerson,
  deploymentUsageItems,
  operationalAssets,
  personNamesMatch,
} from './deploymentUsageModel';
import { deploymentDayCount } from './deploymentDashboardModel';
import { readTagAssetId, resolveTagHandoff, stripTagAssetQuery } from './tagHandoffModel';
import './tracking.css';
import './my-gear.css';

function todayLocal() {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
}

function deploymentTypeLabel(deployment) {
  return deployment?.deploymentType || deployment?.role || 'EMPF';
}

function incidentLabel(item) {
  const name = String(item?.incident || item?.name || '').trim();
  const orderNumber = String(item?.incidentOrderNumber || '').trim();
  return orderNumber ? `${name} — ${orderNumber}` : name;
}

function collectPeople(data) {
  const sources = [data?.medicsAvailable, data?.emtsAvailable, data?.deployed];
  const names = new Set();
  sources.flat().filter(Boolean).forEach(person => {
    const name = String(person?.name || person?.employee || person?.employeeName || '').trim();
    if (name) names.add(name);
  });
  return [...names];
}

function mergeUsedItems(current, incoming) {
  const map = new Map();
  [...(current || []), ...(incoming || [])].forEach(item => {
    const key = `${item.deploymentId}|${normalizeAssetId(item.assetId)}|${item.catalogId}`;
    const existing = map.get(key);
    if (existing) existing.quantity = Math.max(existing.quantity, Number(item.quantity || 0));
    else map.set(key, { ...item, assetId: normalizeAssetId(item.assetId), quantity: Number(item.quantity || 0) });
  });
  return [...map.values()].filter(item => item.quantity > 0);
}


function isRadioClamshellAsset(asset) {
  const id = normalizeAssetId(asset?.id);
  const category = String(asset?.category || '').trim().toLowerCase();
  const name = String(asset?.name || '').trim().toLowerCase();
  return id.startsWith('CLAMSHELL-') || category === 'radio clamshell' || name.includes('radio clamshell');
}

function Home({ state, setView, actorName, onOpenAsset, member, handoffSessions = [], returnCustody = [] }) {
  const active = activeDeploymentsForPerson(state, actorName);
  const activeIds = new Set(active.map(item => item.id));
  const assets = operationalAssets(state);
  const deployed = assets.filter(asset => asset.currentDeploymentId && activeIds.has(asset.currentDeploymentId));
  const personal = assets.filter(asset => asset.ownership === 'personal' && personNamesMatch(asset.personalOwner, actorName));
  const pendingReturnByAsset = new Map();
  handoffSessions
    .filter(session => session?.handoffType === 'return_on_behalf' && session?.status === 'accepted')
    .forEach(session => (session.acceptedAssetIds || []).forEach(id => {
      if (!(session.returnedAssetIds || []).includes(id)) pendingReturnByAsset.set(normalizeAssetId(id), session);
    }));

  const custodyRows = returnCustody.flatMap(session =>
    (session.assets || [])
      .filter(asset => asset.accepted && !asset.returned)
      .map(asset => ({ session, asset }))
  );

  return (
    <>
      <DoorAccessTile member={member} onOpen={() => setView('door-access')} />

      <div className="tracking-hero-actions my-gear-actions">
        <button className="tracking-action tracking-action-deploy" onClick={() => setView('deploy')} disabled={active.length > 0}>
          <span>{active.length ? 'DEPLOYED' : 'DEPLOY'}</span>
          <small>{active.length ? 'Return the current deployment before starting another' : 'Scan the physical gear you are taking'}</small>
        </button>
        <button className="tracking-action tracking-action-return" onClick={() => setView('return')}>
          <span>RETURN</span>
          <small>Return assigned gear and submit used supplies</small>
        </button>
        <button className="tracking-action" onClick={() => setView('transfer')}>
          <span>TRANSFER / RECEIVE</span>
          <small>{deployed.some(asset => asset.ownership !== 'personal') ? 'Hand off program gear or scan a handoff QR to receive' : 'Scan a handoff QR to receive gear'}</small>
        </button>
      </div>

      {active.map(deployment => {
        const usage = deploymentUsageItems(state, deployment.id);
        const assigned = deployed.filter(asset => asset.currentDeploymentId === deployment.id);
        return (
          <section className="tracking-card my-gear-deployment" key={deployment.id}>
            <div className="my-gear-deployment-head">
              <div>
                <span className="my-gear-eyebrow">CURRENT DEPLOYMENT</span>
                <h2>{deployment.incident}</h2>
                <p>{deploymentTypeLabel(deployment)}{deployment.incidentOrderNumber ? ` · ${deployment.incidentOrderNumber}` : ''}</p>
              </div>
              <div className="my-gear-day">Day {deploymentDayCount(deployment)}</div>
            </div>
            <div className="my-gear-deployment-meta">
              <span>{assigned.length} physical assets assigned</span>
              <span>{usage.reduce((sum, item) => sum + Number(item.quantity || 0), 0)} supplies reported used</span>
            </div>
            <div className="my-gear-grid">
              {assigned.map(asset => (
                <button type="button" className="my-gear-card" key={asset.id} onClick={() => onOpenAsset(asset.id)}>
                  <strong>{asset.name}</strong>
                  <small>{asset.id}</small>
                  {pendingReturnByAsset.has(normalizeAssetId(asset.id))
                    ? <span>Pending Return · with {pendingReturnByAsset.get(normalizeAssetId(asset.id))?.receivingName || 'receiving member'}</span>
                    : <span>Open inventory →</span>}
                </button>
              ))}
            </div>
          </section>
        );
      })}

      {custodyRows.length > 0 && (
        <section className="tracking-card">
          <div className="tracking-section-head">
            <div><h2>Gear I'm Returning for Someone Else</h2><p>You have physical return custody only. The original member's operational assignment remains intact until this gear is checked back into the cache.</p></div>
            <button type="button" className="tracking-link-btn" onClick={() => setView('return')}>Return gear</button>
          </div>
          <div className="my-gear-grid">
            {custodyRows.map(({ session, asset }) => (
              <button type="button" className="my-gear-card" key={session.id + '-' + asset.id} onClick={() => setView('return')}>
                <strong>{asset.name || asset.id}</strong>
                <small>{asset.id}</small>
                <span>Pending Return · for {session.outgoingName}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {personal.length > 0 && (
        <section className="tracking-card">
          <div className="tracking-section-head"><div><h2>Assigned to Me</h2><p>Personal or permanently assigned accountable gear.</p></div></div>
          <div className="my-gear-grid">
            {personal.map(asset => (
              <button type="button" className="my-gear-card" key={asset.id} onClick={() => onOpenAsset(asset.id)}>
                <strong>{asset.name}</strong>
                <small>{asset.id}</small>
                <span className={`tracking-pill tracking-${asset.status}`}>{String(asset.status || 'available').replaceAll('_', ' ')}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {!active.length && !personal.length && !custodyRows.length && (
        <section className="tracking-card my-gear-empty">
          <h2>No gear assigned right now</h2>
          <p>When you deploy, scan each physical asset you take. It will appear here until it is returned.</p>
        </section>
      )}
    </>
  );
}

function DeployView({
  state,
  actions,
  incidents,
  incidentFeedStatus,
  setView,
  actorName,
  initialAssetId = '',
  onInitialAssetConsumed,
}) {
  const [incidentInput, setIncidentInput] = useState('');
  const [deploymentType, setDeploymentType] = useState('EMPF');
  const [startDate, setStartDate] = useState(todayLocal());
  const [loanerRadio, setLoanerRadio] = useState('');
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState('');
  const [completion, setCompletion] = useState(null);
  const [expirationGateStatus, setExpirationGateStatus] = useState('idle');
  const deploymentPerson = actorName;
  const identityForFiltering = actorName;
  const active = activeDeploymentsForPerson(state, identityForFiltering);
  const available = operationalAssets(state).filter(asset => {
    if (asset.status !== 'available' || asset.condition !== 'ready') return false;
    if (asset.ownership !== 'personal') return true;
    return personNamesMatch(asset.personalOwner, identityForFiltering);
  });

  const incidentOptions = useMemo(() => {
    const options = [];
    const seen = new Set();
    const add = option => {
      const name = String(option?.name || option?.incident || '').trim();
      if (!name) return;
      const incidentOrderNumber = String(option?.incidentOrderNumber || '').trim().toUpperCase();
      const display = incidentOrderNumber ? `${name} — ${incidentOrderNumber}` : name;
      const key = display.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      options.push({
        name,
        incidentOrderNumber,
        incidentIrwinId: String(option?.incidentIrwinId || option?.irwinId || '').trim(),
        incidentSource: String(option?.incidentSource || option?.source || '').trim(),
        display,
      });
    };
    incidents.forEach(add);
    state.deployments.forEach(add);
    state.recentIncidents.forEach(name => add({ name }));
    return options;
  }, [incidents, state.deployments, state.recentIncidents]);

  const addScan = id => {
    setError('');
    const normalized = normalizeAssetId(id);
    if (!normalized) return { status: 'error', message: 'That tag did not contain a valid Line Medic asset ID.' };

    const asset = available.find(item => normalizeAssetId(item.id) === normalized);
    if (!asset) {
      return {
        status: 'error',
        message: `Asset ${normalized} is not available and ready for you.`,
      };
    }

    if (selected.includes(normalized)) {
      return {
        status: 'duplicate',
        message: `${asset.name || normalized} — ${normalized} is already added.`,
      };
    }

    setSelected(current => current.includes(normalized) ? current : [...current, normalized]);
    return {
      status: 'added',
      message: `${asset.name || normalized} — ${normalized} added.`,
    };
  };

  const initialNormalized = normalizeAssetId(initialAssetId);
  useEffect(() => {
    if (!initialNormalized) return;
    const match = available.find(item => normalizeAssetId(item.id) === initialNormalized);
    if (!match) return;
    setSelected(current => current.includes(initialNormalized) ? current : [...current, initialNormalized]);
    onInitialAssetConsumed?.(initialNormalized);
  }, [initialNormalized, available, onInitialAssetConsumed]);

  const removeAsset = id => setSelected(current => current.filter(item => item !== normalizeAssetId(id)));

  const submit = event => {
    event.preventDefault();
    setError('');
    try {
      if (active.length) throw new Error('You already have an active deployment. Return that gear before starting another deployment.');
      if (!selected.length) throw new Error('Scan each physical gear asset you are taking.');
      if (expirationGateStatus === 'checking') throw new Error('Checking selected gear for expiring inventory. Try again in a moment.');
      if (expirationGateStatus === 'blocked') throw new Error('Replace expiring inventory before deployment.');

      const enteredIncident = incidentInput.trim();
      const matchedIncident = incidentOptions.find(option => option.display.toLowerCase() === enteredIncident.toLowerCase());
      const incident = matchedIncident?.name || enteredIncident;
      if (!incident) throw new Error('Choose or enter the incident.');

      actions.checkout({
        person: deploymentPerson,
        incident,
        incidentOrderNumber: matchedIncident?.incidentOrderNumber || '',
        incidentIrwinId: matchedIncident?.incidentIrwinId || '',
        incidentSource: matchedIncident?.incidentSource || (matchedIncident ? 'NIFC IMSR' : 'Manual'),
        deploymentType,
        startDate,
        loanerRadio,
        assetIds: selected,
        componentSelections: {},
        actor: deploymentPerson,
      });
      setCompletion({ member: deploymentPerson, incident, deploymentType, gearCount: selected.length });
    } catch (err) {
      setError(err?.message || 'Unable to start deployment.');
    }
  };

  if (completion) {
    return (
      <WorkflowComplete
        eyebrow="Deployment started"
        title={`${completion.incident} is now in My Gear`}
        summary="Every scanned physical asset is assigned to you until it is returned."
        details={[
          { label: 'Member', value: completion.member },
          { label: 'Deployment type', value: completion.deploymentType },
          { label: 'Physical gear', value: `${completion.gearCount} assets` },
        ]}
        primaryLabel="Open My Gear"
        onPrimary={() => setView('home')}
      />
    );
  }

  return (
    <section className="tracking-card">
      <div className="tracking-section-head">
        <div><h2>Deploy</h2><p>Scan every physical asset you are taking. No sets or bundle selections.</p></div>
        <button type="button" className="tracking-link-btn" onClick={() => setView('home')}>Back</button>
      </div>
      <div className="tracking-identity-note">
        <strong>Deploying member:</strong>{' '}
{actorName}
      </div>
      <form className="tracking-form" onSubmit={submit}>
        <label>Incident
          <IncidentPicker value={incidentInput} onChange={setIncidentInput} options={incidentOptions} feedStatus={incidentFeedStatus} />
        </label>
        <div className="tracking-form-grid">
          <label>Deployment type
            <select value={deploymentType} onChange={event => setDeploymentType(event.target.value)}>
              {DEPLOYMENT_TYPES.map(type => <option key={type}>{type}</option>)}
            </select>
          </label>
          <label>Deployment date<input type="date" value={startDate} onChange={event => setStartDate(event.target.value)} required /></label>
        </div>
        <label>Loaner radio ID <span className="tracking-optional">optional</span><input value={loanerRadio} onChange={event => setLoanerRadio(event.target.value)} placeholder="KNG 17" /></label>

        <div className="tracking-gear-block">
          <h3>Scan physical gear</h3>
          <QrScanner onScan={addScan} label="Scan gear QR / NFC ID" selectedCount={selected.length} />
          {selected.length > 0 && (
            <div className="my-gear-selected-list">
              {selected.map(id => {
                const asset = available.find(item => normalizeAssetId(item.id) === id);
                return (
                  <div key={id} className="tracking-row">
                    <div><strong>{asset?.name || id}</strong><small>{id}</small></div>
                    <button type="button" className="tracking-link-btn" onClick={() => removeAsset(id)}>Remove</button>
                  </div>
                );
              })}
            </div>
          )}
          <details className="my-gear-manual-fallback">
            <summary>Can't scan? Choose available gear manually</summary>
            <div className="tracking-asset-chips">
              {available.map(asset => {
                const id = normalizeAssetId(asset.id);
                const isSelected = selected.includes(id);
                return (
                  <button type="button" key={id} className={`tracking-asset-chip${isSelected ? ' selected' : ''}`} onClick={() => isSelected ? removeAsset(id) : addScan(id)}>
                    <strong>{asset.name}</strong><small>{id}</small>
                  </button>
                );
              })}
            </div>
          </details>
        </div>

        <DeploymentExpirationGate assetIds={selected} onStatusChange={setExpirationGateStatus} />
        <button type="submit" className="tracking-btn tracking-btn-primary" disabled={['checking', 'blocked'].includes(expirationGateStatus)}>
          {expirationGateStatus === 'checking' ? 'Checking inventory…' : expirationGateStatus === 'blocked' ? 'Replace expiring inventory first' : 'Start Deployment'}
        </button>
      </form>
      <WorkflowAlert message={error} title="Deployment couldn't start" onClose={() => setError('')} />
    </section>
  );
}

function ReturnView({
  state,
  actions,
  setView,
  actorName,
  initialAssetId = '',
  onInitialAssetConsumed,
  returnCustody = [],
  onHandoffRefresh,
}) {
  const [selected, setSelected] = useState([]);
  const [conditions, setConditions] = useState({});
  const [radios, setRadios] = useState({});
  const [clamshellBatteryChecks, setClamshellBatteryChecks] = useState({});
  const [suppliesUsed, setSuppliesUsed] = useState(null);
  const [usedItems, setUsedItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [completion, setCompletion] = useState(null);

  const myDeployments = activeDeploymentsForPerson(state, actorName);
  const deploymentIds = new Set(myDeployments.map(item => item.id));
  const custodyByAssetId = new Map();
  returnCustody.forEach(session => {
    (session.acceptedAssetIds || []).forEach(id => {
      const normalized = normalizeAssetId(id);
      if (!(session.returnedAssetIds || []).map(normalizeAssetId).includes(normalized)) custodyByAssetId.set(normalized, session);
    });
  });
  const deployedAssets = operationalAssets(state).filter(asset => {
    if (!asset.currentDeploymentId) return false;
    const id = normalizeAssetId(asset.id);
    if (deploymentIds.has(asset.currentDeploymentId)) return true;
    const custody = custodyByAssetId.get(id);
    return Boolean(custody && custody.sourceDeploymentId === asset.currentDeploymentId);
  });

  const addScan = id => {
    setError('');
    const normalized = normalizeAssetId(id);
    if (!normalized) return { status: 'error', message: 'That tag did not contain a valid Line Medic asset ID.' };

    const asset = deployedAssets.find(item => normalizeAssetId(item.id) === normalized);
    if (!asset) {
      return {
        status: 'error',
        message: `Asset ${normalized} is not assigned to you and is not in your accepted return custody.`,
      };
    }

    if (selected.includes(normalized)) {
      return {
        status: 'duplicate',
        message: `${asset.name || normalized} — ${normalized} is already added for return.`,
      };
    }

    const deployment = state.deployments.find(item => item.id === asset.currentDeploymentId);
    setSelected(current => current.includes(normalized) ? current : [...current, normalized]);
    setConditions(current => ({ ...current, [normalized]: current[normalized] || 'ready' }));
    const reported = deployment ? deploymentUsageItems(state, deployment.id).filter(item => item.assetId === normalized) : [];
    if (reported.length) {
      setUsedItems(current => mergeUsedItems(current, reported));
      setSuppliesUsed(true);
    }

    return {
      status: 'added',
      message: `${asset.name || normalized} — ${normalized} added for return.`,
    };
  };

  const initialNormalized = normalizeAssetId(initialAssetId);
  useEffect(() => {
    if (!initialNormalized) return;
    const match = deployedAssets.find(item => normalizeAssetId(item.id) === initialNormalized);
    if (!match) return;
    const deployment = state.deployments.find(item => item.id === match.currentDeploymentId);
    setSelected(current => current.includes(initialNormalized) ? current : [...current, initialNormalized]);
    setConditions(current => ({ ...current, [initialNormalized]: current[initialNormalized] || 'ready' }));
    const reported = deployment ? deploymentUsageItems(state, deployment.id).filter(item => item.assetId === initialNormalized) : [];
    if (reported.length) {
      setUsedItems(current => mergeUsedItems(current, reported));
      setSuppliesUsed(true);
    }
    onInitialAssetConsumed?.(initialNormalized);
  }, [initialNormalized, deployedAssets, state.deployments, onInitialAssetConsumed]);

  const removeAsset = id => {
    const normalized = normalizeAssetId(id);
    setSelected(current => current.filter(item => item !== normalized));
    setUsedItems(current => current.filter(item => normalizeAssetId(item.assetId) !== normalized));
    setClamshellBatteryChecks(current => {
      const next = { ...current };
      delete next[normalized];
      return next;
    });
  };

  const selectedAssetDetails = selected.map(id => {
    const asset = deployedAssets.find(item => normalizeAssetId(item.id) === id);
    const custody = custodyByAssetId.get(id) || null;
    return asset ? { id, name: asset.name, deploymentId: asset.currentDeploymentId, custody } : null;
  }).filter(Boolean);

  const selectedDeploymentIds = new Set(selectedAssetDetails.map(item => item.deploymentId));
  const selectedDeployments = state.deployments.filter(item => selectedDeploymentIds.has(item.id));
  const usedAssetIds = useMemo(() => new Set(usedItems.map(item => normalizeAssetId(item.assetId))), [usedItems]);

  const createRestockRequests = async () => {
    const token = await getMemberAccessToken();
    if (!token) throw new Error('Member sign in required to create a restock request.');
    const grouped = new Map();
    usedItems.forEach(item => {
      const list = grouped.get(item.deploymentId) || [];
      list.push(item);
      grouped.set(item.deploymentId, list);
    });
    const codes = [];
    for (const [deploymentId, items] of grouped.entries()) {
      const deployment = state.deployments.find(item => item.id === deploymentId);
      const assetIds = selectedAssetDetails.filter(item => item.deploymentId === deploymentId).map(item => item.id);
      const response = await fetch('/api/member-tracking?mode=restock-create', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deploymentId,
          person: deployment.person,
          incident: deployment.incident,
          incidentOrderNumber: deployment.incidentOrderNumber || '',
          deploymentType: deploymentTypeLabel(deployment),
          assetIds,
          items,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to create the restock request.');
      if (payload?.request?.request_code) codes.push(payload.request.request_code);
    }
    return codes;
  };

  const submit = async event => {
    event.preventDefault();
    setError('');
    if (!selected.length) return setError('Scan or select the gear you are returning.');
    const unconfirmedClamshell = selected.find(assetId => {
      const asset = deployedAssets.find(item => normalizeAssetId(item.id) === assetId);
      return isRadioClamshellAsset(asset) && !clamshellBatteryChecks[assetId];
    });
    if (unconfirmedClamshell) return setError('Confirm all batteries have been removed from each radio clamshell before completing the return.');
    if (suppliesUsed === null) return setError('Confirm whether any medical supplies were used.');
    if (suppliesUsed && !usedItems.length) return setError('Add the supplies that were used, or choose No supplies used.');

    setSubmitting(true);
    try {
      const requestCodes = suppliesUsed ? await createRestockRequests() : [];
      const returns = selected.map(assetId => {
        const selectedCondition = conditions[assetId] || 'ready';
        const condition = selectedCondition === 'damaged' ? 'damaged' : usedAssetIds.has(assetId) ? 'needs_restock' : selectedCondition;
        const custody = custodyByAssetId.get(assetId);
        return {
          assetId,
          condition,
          ...(custody ? {
            handoffSessionId: custody.id,
            handoffType: 'return_on_behalf',
            returnedOnBehalfOf: custody.outgoingName || '',
            returnedOnBehalfOfUserId: custody.outgoingUserId || '',
          } : {}),
        };
      });
      actions.returnAssets({ returnedBy: actorName, returns, loanerReturnedByDeployment: radios });
      await actions.flushSync?.();

      const custodySessionIds = [...new Set(returns.map(item => item.handoffSessionId).filter(Boolean))];
      for (const sessionId of custodySessionIds) {
        await callGearHandoff('complete_return', { sessionId });
      }
      if (custodySessionIds.length) await onHandoffRefresh?.();

      setCompletion({ returned: returns.length, attention: returns.filter(item => item.condition !== 'ready').length, requestCodes });
    } catch (err) {
      setError(err?.message || 'Unable to complete return.');
    } finally {
      setSubmitting(false);
    }
  };

  if (completion) {
    return (
      <WorkflowComplete
        eyebrow="Return complete"
        title="Your gear return is recorded"
        summary={completion.requestCodes.length ? 'The used-supply list was submitted with the return.' : 'No supply request was needed.'}
        details={[
          { label: 'Gear returned', value: completion.returned },
          { label: 'Needs attention', value: completion.attention },
          ...(completion.requestCodes.length ? [{ label: 'Restock request', value: completion.requestCodes.join(', ') }] : []),
        ]}
        primaryLabel="Back to My Gear"
        onPrimary={() => setView('home')}
      />
    );
  }

  return (
    <section className="tracking-card">
      <div className="tracking-section-head">
        <div><h2>Return</h2><p>Scan the gear coming back. Anything you marked used during the deployment is already on the restock list.</p></div>
        <button type="button" className="tracking-link-btn" onClick={() => setView('home')}>Back</button>
      </div>
      <form className="tracking-form" onSubmit={submit}>
        <QrScanner onScan={addScan} label="Scan returned gear" selectedCount={selected.length} />
        <div className="tracking-asset-chips">
          {deployedAssets.map(asset => {
            const id = normalizeAssetId(asset.id);
            const deployment = getDeploymentForAsset(state, asset);
            const isSelected = selected.includes(id);
            return (
              <button type="button" key={id} className={`tracking-asset-chip${isSelected ? ' selected' : ''}`} onClick={() => isSelected ? removeAsset(id) : addScan(id)}>
                <strong>{asset.name}</strong><small>{deployment ? `${incidentLabel(deployment)} · ${id}` : id}</small>
              </button>
            );
          })}
        </div>
        {!deployedAssets.length && <div className="tracking-empty">You do not have any physical gear checked out right now.</div>}

        {selected.map(id => {
          const asset = deployedAssets.find(item => normalizeAssetId(item.id) === id);
          const deployment = getDeploymentForAsset(state, asset);
          return (
            <div className="tracking-return-item" key={id}>
              <div>
                <strong>{asset?.name}</strong>
                <small>{incidentLabel(deployment)}</small>
                {custodyByAssetId.has(id) && <small>Returning on behalf of {custodyByAssetId.get(id)?.outgoingName}</small>}
                {usedAssetIds.has(id) && <small>Used supplies reported · returns as Needs Restock</small>}
                {isRadioClamshellAsset(asset) && (
                  <label className="tracking-checkbox">
                    <input
                      type="checkbox"
                      checked={Boolean(clamshellBatteryChecks[id])}
                      onChange={event => setClamshellBatteryChecks(current => ({ ...current, [id]: event.target.checked }))}
                    />
                    I confirm all batteries have been removed from this radio clamshell.
                  </label>
                )}
              </div>
              <select value={conditions[id] || 'ready'} onChange={event => setConditions(current => ({ ...current, [id]: event.target.value }))}>
                {RETURN_CONDITIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
          );
        })}

        {selected.length > 0 && (
          <UsedSupplyReporter selectedAssets={selectedAssetDetails} hasUse={suppliesUsed} onHasUseChange={setSuppliesUsed} value={usedItems} onChange={setUsedItems} />
        )}

        {selectedDeployments.filter(item => item.loanerRadio && personNamesMatch(item.person, actorName)).map(deployment => (
          <label className="tracking-checkbox" key={deployment.id}>
            <input type="checkbox" checked={Boolean(radios[deployment.id])} onChange={event => setRadios(current => ({ ...current, [deployment.id]: event.target.checked }))} />
            Loaner radio {deployment.loanerRadio} returned
          </label>
        ))}

        <button type="submit" className="tracking-btn tracking-btn-primary" disabled={submitting || !selected.length}>{submitting ? 'Completing return…' : 'Complete Return'}</button>
      </form>
      <WorkflowAlert message={error} title="Return couldn't continue" onClose={() => setError('')} />
    </section>
  );
}

export default function MyGearApp({ member }) {
  const { state, actions, sync } = useTrackingStore();
  const [view, setView] = useState('home');
  const [assetId, setAssetId] = useState('');
  const [incidents, setIncidents] = useState([]);
  const [incidentFeedStatus, setIncidentFeedStatus] = useState('loading');
  const [pendingTagAssetId, setPendingTagAssetId] = useState(() => readTagAssetId(window.location.search));
  const [tagTarget, setTagTarget] = useState('');
  const [tagResolved, setTagResolved] = useState(() => !readTagAssetId(window.location.search));
  const [tagNotice, setTagNotice] = useState('');
  const actorName = String(member?.full_name || '').trim();
  const handoffs = useGearHandoffs(member?.id);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/incidents')
      .then(response => response.ok ? response.json() : Promise.reject(new Error('Incident feed unavailable')))
      .then(data => {
        if (cancelled) return;
        setIncidents(Array.isArray(data?.incidents) ? data.incidents : []);
        setIncidentFeedStatus('ready');
      })
      .catch(() => { if (!cancelled) setIncidentFeedStatus('error'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!pendingTagAssetId || tagResolved || !actorName || sync.status === 'connecting') return;

    const resolution = resolveTagHandoff(state, actorName, pendingTagAssetId);
    if (resolution.status === 'missing' && sync.status === 'syncing') return;

    if (resolution.status === 'missing' && sync.status === 'error') {
      setTagNotice(`Could not verify ${pendingTagAssetId} because Tracking is unavailable. Try the tag again when the connection is restored.`);
      setTagTarget('');
      setView('home');
    } else {
      setTagNotice(resolution.message);
      setTagTarget(resolution.target === 'deploy' || resolution.target === 'return' ? resolution.target : '');
      setView(resolution.target);
    }

    setTagResolved(true);
    if (window.history?.replaceState) {
      window.history.replaceState({}, '', stripTagAssetQuery(window.location.href));
    }
  }, [pendingTagAssetId, tagResolved, actorName, sync.status, state]);

  const selectedAsset = operationalAssets(state).find(item => normalizeAssetId(item.id) === normalizeAssetId(assetId)) || null;
  const selectedDeployment = selectedAsset?.currentDeploymentId ? state.deployments.find(item => item.id === selectedAsset.currentDeploymentId) || null : null;

  const openAsset = id => {
    setAssetId(normalizeAssetId(id));
    setView('asset');
  };

  const consumeTagHandoff = () => {
    setPendingTagAssetId('');
    setTagTarget('');
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.assign('/');
  };

  return (
    <div className="tracking-root">
      <header className="tracking-topbar">
        <div>
          <h1>My Gear</h1>
          <p>
            {actorName}{' · '}deploy, view your actual bag inventory, report usage, and return gear
          </p>
        </div>
        <div className="tracking-member-actions">
          <div className="tracking-offline-badge">{sync.status === 'synced' ? 'Synced' : sync.status === 'offline' ? 'Offline-ready' : 'Syncing'}</div>
          <button type="button" className="tracking-signout" onClick={() => window.location.assign('/')}>Dashboard</button>
          <button type="button" className="tracking-signout" onClick={signOut}>Sign out</button>
        </div>
      </header>

      <main className="tracking-page">
        {tagNotice && (
          <div className="tracking-identity-note" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span><strong>Tag recognized:</strong> {tagNotice}</span>
            <button type="button" className="tracking-link-btn" onClick={() => setTagNotice('')}>Dismiss</button>
          </div>
        )}
        {view === 'home' && <Home state={state} setView={setView} actorName={actorName} onOpenAsset={openAsset} member={member} handoffSessions={handoffs.outgoing} returnCustody={handoffs.activeReturnCustody} />}
        {view === 'door-access' && <DoorAccessView member={member} onBack={() => setView('home')} />}
        {view === 'deploy' && (
          <DeployView
            state={state}
            actions={actions}
            incidents={incidents}
            incidentFeedStatus={incidentFeedStatus}
            setView={setView}
            actorName={actorName}
            initialAssetId={tagTarget === 'deploy' ? pendingTagAssetId : ''}
            onInitialAssetConsumed={consumeTagHandoff}
          />
        )}
        {view === 'transfer' && (
          <TransferGearView
            state={state}
            actorName={actorName}
            onBack={() => setView('home')}
            onRefreshSessions={handoffs.refresh}
          />
        )}
        {view === 'return' && (
          <ReturnView
            state={state}
            actions={actions}
            setView={setView}
            actorName={actorName}
            initialAssetId={tagTarget === 'return' ? pendingTagAssetId : ''}
            onInitialAssetConsumed={consumeTagHandoff}
            returnCustody={handoffs.activeReturnCustody}
            onHandoffRefresh={handoffs.refresh}
          />
        )}
        {view === 'asset' && selectedAsset && <GearInventoryView asset={selectedAsset} deployment={selectedDeployment} state={state} actions={actions} actorName={actorName} onBack={() => setView('home')} />}
      </main>

      <nav className="tracking-nav" aria-label="My Gear navigation">
        <button className={view === 'home' || view === 'asset' || view === 'door-access' ? 'active' : ''} onClick={() => setView('home')}>My Gear</button>
        <button className={view === 'deploy' ? 'active' : ''} onClick={() => setView('deploy')}>Deploy</button>
        <button className={view === 'transfer' ? 'active' : ''} onClick={() => setView('transfer')}>Transfer</button>
        <button className={view === 'return' ? 'active' : ''} onClick={() => setView('return')}>Return</button>
      </nav>
    </div>
  );
}
